import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, X } from 'lucide-react';
import './style/MomentsFeedV2.css';
import { host } from './script/variables';
import BottomNav from './BottomNav';
import Comments from './Comments';

// ─── Helpers ────────────────────────────────────────────────────────────────

const safeStr = (v) => (v != null && typeof v === 'string' ? v : '');

const getUserLabel = (user) => {
  if (!user) return 'Anonymous';
  return (
    safeStr(user.displayName) ||
    safeStr(user.username) ||
    `${safeStr(user.firstName)} ${safeStr(user.lastName)}`.trim() ||
    'Anonymous'
  );
};

const getUserAvatar = (user) => {
  const img = user?.image;
  if (img && typeof img === 'string' && img !== '#') return img;
  return null;
};

const timeAgo = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const fmtCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return String(num);
};

const categoryEmoji = (cat) => {
  const map = { cafe: '☕', restaurant: '🍽️', bar: '🍸', bakery: '🥐' };
  const key = typeof cat === 'string' ? cat.toLowerCase() : '';
  return map[key] || '📍';
};

function getToken() {
  return localStorage.getItem('token') ?? '';
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('cafeuser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const BATCH_SIZE = 5;
const PREFETCH_WHEN_REMAINING = 2; // fetch next batch when only this many unseen cards are left

// How long the filter pills stay visible after the user scrolls up
// (or on first load) before auto-hiding themselves.
const FILTER_AUTOHIDE_MS = 2500;
// Ignore sub-pixel/jitter scroll deltas so momentum scrolling doesn't
// flicker the pills open and shut.
const SCROLL_DIRECTION_THRESHOLD = 4;

// ─── Action button (lucide icon, CSS-driven micro-interaction) ─────────────
//
// bidirectional=true  -> like button: icon fills solid + scale-pops when it becomes active,
//                         and calmly reverts when it becomes inactive (no pop on unlike).
// bidirectional=false -> comment/share: a single one-shot scale-bounce on every click.
//
// The animation itself lives in CSS (see snippet below the component) using two
// classes toggled on/off the button: `pop` (bidirectional activation) and `bounce`
// (one-shot). Both are removed after the animation ends so they can retrigger.

const ActionButton = ({
  icon: Icon,
  size = 26,
  onClick,
  label,
  count,
  active,
  bidirectional = false,
}) => {
  const [pop, setPop] = useState(false);
  const [bounce, setBounce] = useState(false);
  const prevActive = useRef(active);

  useEffect(() => {
    if (bidirectional && active && !prevActive.current) {
      setPop(true);
    }
    prevActive.current = active;
  }, [active, bidirectional]);

  const handleClick = (e) => {
    onClick?.(e);
    if (!bidirectional) {
      setBounce(true);
    }
  };

  return (
    <button
      className={`rl-action-btn ${active ? 'active' : ''}`}
      onClick={handleClick}
      aria-label={label}
      aria-pressed={bidirectional ? !!active : undefined}
    >
      <Icon
        size={size}
        className={[
          'rl-action-icon',
          active ? 'rl-icon-filled' : '',
          pop ? 'pop' : '',
          bounce ? 'bounce' : '',
        ].filter(Boolean).join(' ')}
        fill={active ? 'currentColor' : 'none'}
        strokeWidth={active ? 0 : 2}
        onAnimationEnd={() => { setPop(false); setBounce(false); }}
      />
      {count !== undefined && <span className="rl-action-count">{fmtCount(count)}</span>}
    </button>
  );
};

// ─── Skeleton screen ────────────────────────────────────────────────────────

const SkeletonScreen = () => (
  <div className="rl-card rl-skeleton" aria-hidden="true">
    <div className="rl-skeleton-shimmer" />
  </div>
);

// ─── Comments Drawer ─────────────────────────────────────────────────────────

const CommentsDrawer = ({ moment, isOpen, onClose, isLogin, onCommentCountChange }) => {
  const [commentCount, setCommentCount] = useState(moment.commentCount ?? 0);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    onCommentCountChange?.(commentCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentCount]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`rl-drawer-backdrop ${isOpen ? 'open' : ''}`}
      onClick={handleBackdrop}
      aria-hidden={!isOpen}
    >
      <div className={`rl-drawer ${isOpen ? 'open' : ''}`} role="dialog" aria-label="Comments" aria-modal="true">
        <div className="rl-drawer-handle" />
        <div className="rl-drawer-header">
          <span className="rl-drawer-title">💬 {commentCount} comments</span>
          <button className="rl-drawer-close" onClick={onClose} aria-label="Close comments">
            <X size={18} />
          </button>
        </div>
        <div className="rl-drawer-body">
          {isOpen && (
            <Comments
              postid={moment._id}
              commentCount={commentCount}
              setCommentCount={setCommentCount}
              isLogin={isLogin}
              isBlog={false}
              isMoment={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Reel Card (one full-screen moment) ─────────────────────────────────────

const ReelCard = ({ moment, index, isLogin, isActive, registerRef, onOpenComments }) => {
  const navigate = useNavigate();

  const [imgLoaded, setImgLoaded] = useState(false);
  const [liked, setLiked] = useState(!!moment.isLiked);
  const [likeCount, setLikeCount] = useState(Number(moment.likes) || 0);
  const [commentCount, setCommentCount] = useState(Number(moment.commentCount) || 0);
  const [showBurst, setShowBurst] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);

  const lastTap = useRef(0);
  const burstTimer = useRef(null);
  const momentIdRef = useRef(moment._id);
  momentIdRef.current = moment._id;

  const userName = getUserLabel(moment.user);
  const avatar = getUserAvatar(moment.user);
  const userId = moment.user?._id;
  const cafeId = moment.cafe?._id;
  const caption = typeof moment.caption === 'string' ? moment.caption : '';
  const isLongCaption = caption.length > 90;
  const postedAgo = timeAgo(moment.createdAt);

  const callLikeApi = useCallback(async (action) => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${host}/api/moments/v2/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ momentId: momentIdRef.current, action }),
      });
      if (!res.ok) console.warn('likeMoment API error:', res.status, await res.text());
    } catch (err) {
      console.error('likeMoment error:', err);
    }
  }, []);

  const triggerLike = useCallback(() => {
    if (liked) return; // double-tap only likes, never unlikes
    setLiked(true);
    setLikeCount((c) => c + 1);
    setShowBurst(true);
    clearTimeout(burstTimer.current);
    burstTimer.current = setTimeout(() => setShowBurst(false), 700);
    if (isLogin) callLikeApi('like');
  }, [liked, isLogin, callLikeApi]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTap.current < 350) triggerLike();
    lastTap.current = now;
  }, [triggerLike]);

  const handleLikeBtn = (e) => {
    e.stopPropagation();
    if (!isLogin) { alert('Please log in to like'); return; }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => (next ? c + 1 : Math.max(0, c - 1)));
    callLikeApi(next ? 'like' : 'unlike');
  };

  const handleComment = (e) => {
    e.stopPropagation();
    onOpenComments(moment, setCommentCount);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareData = {
      title: `${userName} at ${safeStr(moment.cafe?.name) || 'a place'}`,
      text: caption || 'Check out this moment!',
      url: `${window.location.origin}/happymoments`,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setShareToast(true);
        setTimeout(() => setShareToast(false), 2200);
      }
    } catch { /* user cancelled share sheet — no-op */ }
  };

  const goToUser = (e) => {
    e.stopPropagation();
    if (userId) navigate(`/user?id=${userId}`);
  };

  const goToCafe = (e) => {
    e.stopPropagation();
    if (cafeId) navigate(`/moredetails/?cafeid=${cafeId}`);
  };

  return (
    <section
      ref={(el) => registerRef(el, index)}
      data-index={index}
      className={`rl-card ${isActive ? 'is-active' : ''}`}
      onClick={handleDoubleTap}
      aria-label={`Moment by ${userName}`}
    >
      {!imgLoaded && <div className="rl-img-placeholder" />}
      <img
        className={`rl-img ${imgLoaded ? 'loaded' : ''}`}
        src={moment.imageurl}
        alt={caption || 'moment'}
        loading={Math.abs(index) <= 2 ? 'eager' : 'lazy'}
        onLoad={() => setImgLoaded(true)}
      />

      {showBurst && (
        <div className="rl-burst" aria-hidden="true">
          <Heart size={120} className="rl-burst-heart" fill="currentColor" strokeWidth={0} />
        </div>
      )}

      {/* Top: user info. Username + timestamp share one row so the
          time badge sits right next to the name instead of centering
          against the whole two-line block. */}
      <div className="rl-top-strip">
        <button className="rl-avatar-btn" onClick={goToUser} aria-label={`${userName}'s profile`}>
          {avatar ? (
            <img className="rl-avatar-img" src={avatar} alt={userName} />
          ) : (
            <div className="rl-avatar-fallback">{(userName[0] ?? '?').toUpperCase()}</div>
          )}
        </button>
        <div className="rl-top-info">
          <div className="rl-name-row">
            <button className="rl-username" onClick={goToUser}>{userName}</button>
            {postedAgo && <span className="rl-time-badge">{postedAgo}</span>}
          </div>
          <button className="rl-cafename" onClick={goToCafe}>
            {categoryEmoji(moment.cafe?.category)}&nbsp;{safeStr(moment.cafe?.name) || 'Unknown place'}
          </button>
        </div>
      </div>

      {/* Right: action rail */}
      <div className="rl-actions-rail">
        <ActionButton
          icon={Heart}
          bidirectional
          active={liked}
          count={likeCount}
          label={liked ? 'Unlike' : 'Like'}
          onClick={handleLikeBtn}
        />
        <ActionButton
          icon={MessageCircle}
          count={commentCount}
          label="Comments"
          onClick={handleComment}
        />
        <ActionButton
          icon={Share2}
          label="Share"
          onClick={handleShare}
        />
      </div>

      {/* Bottom: caption */}
      {caption && (
        <p
          className={`rl-caption ${captionExpanded ? 'expanded' : ''}`}
          onClick={(e) => { e.stopPropagation(); if (isLongCaption) setCaptionExpanded((v) => !v); }}
        >
          {captionExpanded ? caption : caption.slice(0, 90)}
          {isLongCaption && !captionExpanded && <span className="rl-more-text">… more</span>}
        </p>
      )}

      {shareToast && <div className="rl-share-toast" aria-live="polite">Link copied!</div>}
    </section>
  );
};

// ─── Filter Tabs ─────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'latest',  label: 'Latest' },
  { key: 'popular', label: 'Popular' },
  { key: 'nearby',  label: 'Near Me' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

const MomentsReels = () => {
  const [filter, setFilter]       = useState('latest');
  const [moments, setMoments]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [initialLoad, setInitial] = useState(true);
  const [hasMore, setHasMore]     = useState(true);
  const [error, setError]         = useState(null);
  const [location, setLocation]   = useState(null);
  const [locError, setLocError]   = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeComments, setActiveComments] = useState(null); // { moment, setCommentCount }
  const [isLogin, setIsLogin]     = useState(() => !!(getStoredUser() && getToken()));
  const [filtersVisible, setFiltersVisible] = useState(true);

  const scrollRef      = useRef(null);
  const cardRefs        = useRef(new Map());
  const abortRef        = useRef(null);
  const loadingRef      = useRef(false);
  const hasMoreRef      = useRef(true);
  const cursorRef       = useRef(null);
  const nearbyPageRef   = useRef(0);
  const filterRef       = useRef('latest');
  const locationRef     = useRef(null);
  const activeIndexRef  = useRef(0);
  const momentsLenRef   = useRef(0);

  filterRef.current      = filter;
  locationRef.current    = location;
  hasMoreRef.current     = hasMore;
  loadingRef.current     = loading;
  activeIndexRef.current = activeIndex;
  momentsLenRef.current  = moments.length;

  useEffect(() => {
    const sync = () => setIsLogin(!!(getStoredUser() && getToken()));
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  // ── Geolocation ──────────────────────────────────────────────────────────
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocError('Geolocation not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocError(null);
      },
      () => setLocError('Location access denied. Enable location to see nearby moments.')
    );
  }, []);

  // ── Core fetch — always pulls BATCH_SIZE, stable reference via refs ──────
  const fetchMoments = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    const currentFilter   = filterRef.current;
    const currentLocation = locationRef.current;
    if (currentFilter === 'nearby' && !currentLocation) return;
    if (!reset && !hasMoreRef.current) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ filter: currentFilter, ps: BATCH_SIZE });

    if (currentFilter === 'nearby') {
      const page = reset ? 0 : nearbyPageRef.current;
      params.set('page', page);
      params.set('lat', currentLocation.lat);
      params.set('lng', currentLocation.lng);
    } else {
      const currentCursor = reset ? null : cursorRef.current;
      if (currentCursor) params.set('cursor', currentCursor);
    }

    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token;

    try {
      const res = await fetch(`${host}/api/moments/v2/feed?${params}`, { signal: controller.signal, headers });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();

      setMoments((prev) => (reset ? json.data : [...prev, ...json.data]));

      const nextHasMore = !!json.hasMore;
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);

      if (currentFilter === 'nearby') {
        nearbyPageRef.current = json.nextPage ?? 0;
      } else {
        cursorRef.current = json.nextCursor ?? null;
      }
    } catch (err) {
      if (err.name !== 'AbortError') setError('Something went wrong. Tap to retry.');
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setInitial(false);
    }
  }, []);

  // ── Reset on filter/location change ──────────────────────────────────────
  useEffect(() => {
    if (filter === 'nearby' && !location) {
      requestLocation();
      return;
    }
    setMoments([]);
    setActiveIndex(0);
    cursorRef.current = null;
    nearbyPageRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    setInitial(true);
    fetchMoments(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, location]);

  // ── Prefetch: fire the next batch once only PREFETCH_WHEN_REMAINING unseen cards are left ──
  useEffect(() => {
    const remaining = momentsLenRef.current - 1 - activeIndex;
    if (remaining <= PREFETCH_WHEN_REMAINING && hasMoreRef.current && !loadingRef.current) {
      fetchMoments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, moments.length]);

  // ── Register per-card refs, drive active-index via IntersectionObserver ──
  const registerRef = useCallback((el, index) => {
    const map = cardRefs.current;
    if (el) map.set(index, el);
    else map.delete(index);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number(entry.target.dataset.index);
            setActiveIndex((prev) => (prev === idx ? prev : idx));
          }
        });
      },
      { root: container, threshold: [0.6] }
    );

    cardRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [moments]);

  // ── Keyboard nav (ArrowUp / ArrowDown) ────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const nextIdx = activeIndexRef.current + dir;
      const el = cardRefs.current.get(nextIdx);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // ── Filter pills: hide on scroll-down, show on scroll-up, then
  //    auto-hide again after a few seconds of no further scrolling. ──────────
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    let lastTop = container.scrollTop;
    let ticking = false;
    let hideTimer = null;

    const scheduleAutoHide = () => {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setFiltersVisible(false), FILTER_AUTOHIDE_MS);
    };

    const evaluate = () => {
      ticking = false;
      const top = container.scrollTop;
      const delta = top - lastTop;

      if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD) return;

      if (delta > 0) {
        // Scrolling down (toward the next moment) — get out of the way.
        setFiltersVisible(false);
        clearTimeout(hideTimer);
      } else {
        // Scrolling up (toward a previous moment) — reveal, then
        // auto-hide again after a pause.
        setFiltersVisible(true);
        scheduleAutoHide();
      }
      lastTop = top;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    };

    // Auto-hide the initial view too, once the user has had a moment
    // to see the filters on load.
    scheduleAutoHide();

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      clearTimeout(hideTimer);
    };
  }, []);

  const handleFilterClick = (key) => {
    if (key === filter) return;
    setFilter(key);
    if (key === 'nearby' && !location) requestLocation();
  };

  const handleOpenComments = useCallback((moment, setCommentCount) => {
    setActiveComments({ moment, setCommentCount });
  }, []);

  const showInitialSkeletons = (filter === 'nearby' && !location && !locError) || initialLoad;

  return (
    <div className="rl-root">
      {/* Overlay filter pills — float above the feed, don't take layout space */}
      <div className={`rl-filter-overlay ${filtersVisible ? '' : 'hidden'}`} role="tablist" aria-hidden={!filtersVisible}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            tabIndex={filtersVisible ? 0 : -1}
            className={`rl-filter-pill ${filter === f.key ? 'active' : ''}`}
            onClick={() => handleFilterClick(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filter === 'nearby' && locError && (
        <div className="rl-loc-error">
          <span>📍 {locError}</span>
          <button onClick={requestLocation}>Try again</button>
        </div>
      )}

      <div className="rl-scroll" ref={scrollRef}>
        {showInitialSkeletons &&
          Array.from({ length: 3 }).map((_, i) => <SkeletonScreen key={`sk-${i}`} />)
        }

        {filter === 'nearby' && !location && !locError && (
          <div className="rl-card rl-loc-loading-card">
            <span className="rl-spinner" />
            <p>Finding moments near you…</p>
          </div>
        )}

        {moments.map((m, i) => (
          <ReelCard
            key={m._id}
            moment={m}
            index={i}
            isLogin={isLogin}
            isActive={i === activeIndex}
            registerRef={registerRef}
            onOpenComments={handleOpenComments}
          />
        ))}

        {error && (
          <div className="rl-card rl-error-card">
            <span>{error}</span>
            <button onClick={() => fetchMoments(false)}>Retry</button>
          </div>
        )}

        {!hasMore && !loading && moments.length > 0 && (
          <div className="rl-card rl-end-card">
            <span>you've seen it all ✨</span>
            <p>Follow more people to discover new moments</p>
          </div>
        )}

        {!loading && !initialLoad && moments.length === 0 && !error && (
          <div className="rl-card rl-empty-card">
            <span className="rl-empty-icon">{filter === 'nearby' ? '📍' : '📸'}</span>
            <p>{filter === 'nearby' ? 'No nearby moments' : 'No moments yet'}</p>
            <p className="rl-empty-sub">
              {filter === 'nearby' ? 'Try a different area' : 'Be the first to share one!'}
            </p>
          </div>
        )}
      </div>

      {activeComments && (
        <CommentsDrawer
          moment={activeComments.moment}
          isOpen={!!activeComments}
          onClose={() => setActiveComments(null)}
          isLogin={isLogin}
          onCommentCountChange={activeComments.setCommentCount}
        />
      )}

      <BottomNav />
    </div>
  );
};

export default MomentsReels;